import { cn } from '@/lib/utils';
import Image, { ImageProps } from 'next/image';
import React from 'react';
import { colorPlaceholderBase64 } from '@/lib/image';

type DynamicImageProps = ImageProps & {
  className?: string;
  src: string;
  alt: string;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  sizes?: string;
  onLoadingComplete?: () => void;
  disableAutoFade?: boolean;
};

const DynamicImage = ({
  src,
  alt,
  width = 50,
  height = 50,
  className,
  onLoadingComplete,
  style,
  sizes,
  disableAutoFade = false,
  ...props
}: DynamicImageProps) => {
  const [error, setError] = React.useState<React.SyntheticEvent<HTMLImageElement, Event> | null>(
    null
  );
  const imagePlaceholder = colorPlaceholderBase64();

  const [isLoading, setIsLoading] = React.useState(true);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const imageRef = React.useRef<HTMLImageElement>(null);

  const imageSrc = src;

  React.useEffect(() => {
    setError(null);
    setIsLoading(true);
    setImageLoaded(false);
  }, [src]);

  React.useEffect(() => {
    if (isLoading && !error) {
      const timer = setTimeout(() => {
        if (imageRef.current && imageRef.current.complete) {
          setIsLoading(false);
          setImageLoaded(true);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoading, error]);

  const hasAmbientAnimation = className?.includes('animate-zoomSlideDown');

  const handleLoad = React.useCallback(() => {
    setIsLoading(false);
    setImageLoaded(true);

    if (imageRef.current && !hasAmbientAnimation && !disableAutoFade) {
      imageRef.current.style.opacity = '1';
      void imageRef.current.offsetHeight;
    }

    onLoadingComplete?.();
  }, [onLoadingComplete, hasAmbientAnimation]);

  const handleLoadingComplete = React.useCallback(
    (img: HTMLImageElement) => {
      setIsLoading(false);
      setImageLoaded(true);

      if (!hasAmbientAnimation && !disableAutoFade) {
        img.style.opacity = '1';
        void img.offsetHeight;
      }

      onLoadingComplete?.();
    },
    [onLoadingComplete, hasAmbientAnimation]
  );

  const handleError = React.useCallback((event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setError(event);
    setIsLoading(false);
    setImageLoaded(true);
  }, []);

  return (
    <Image
      ref={imageRef}
      className={cn(
        className,
        !disableAutoFade &&
          !hasAmbientAnimation && [
            isLoading && !imageLoaded ? 'bg-gray-200' : '',
            !imageLoaded ? 'opacity-0' : 'opacity-100',
            'transition-opacity duration-300 ease-in-out',
          ],
        hasAmbientAnimation && [!imageLoaded && 'invisible', imageLoaded && 'visible']
      )}
      src={error ? imagePlaceholder : imageSrc}
      alt={alt}
      {...(props.fill ? { fill: true } : { width, height })}
      sizes={sizes}
      placeholder='blur'
      style={style}
      blurDataURL={`/_next/image?url=${encodeURIComponent(src || '')}&w=16&q=1`}
      onError={handleError}
      onLoad={handleLoad}
      onLoadingComplete={handleLoadingComplete}
      {...props}
    />
  );
};

export default DynamicImage;
